// Agent-fleet page formatters — one system of units for every number on the
// page (review 2026-08-27: the page mixed "17m", "10:38", "2h 29m", "8/25/2026",
// "1h ago", "+<1% wk" — readable only to whoever wrote them).
//
//   durations   → "4 s" · "17 min" · "10 min 38 s" · "2 h 29 min" · "3 d 4 h"
//   moments     → "today 18:29" · "yesterday 14:10" · "Aug 25" · "Aug 25, 2025"
//   clock       → "21:00" (today) · "Fri 17:00" (this week) · "Aug 29, 17:00"
//   quota share → "2%" · "under 1%"   (the provider reports whole percents)
//   relative    → "27 s ago" · "1 min ago" · "3 h ago" · "2 d ago"

const two = (n) => String(n).padStart(2, "0");

// Compact duration. `withSeconds` keeps seconds under an hour ("10 min 38 s")
// for RUNNING jobs, so the card visibly moves between worker ticks.
export function fmtDur(seconds, { withSeconds = false } = {}) {
  if (seconds == null || !Number.isFinite(seconds)) return "—";
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s} s`;
  if (s < 3600) {
    const m = Math.floor(s / 60), sec = s % 60;
    return withSeconds ? `${m} min ${two(sec)} s` : `${Math.round(s / 60)} min`;
  }
  if (s < 86400) {
    const h = Math.floor(s / 3600), m = Math.round((s % 3600) / 60);
    return m ? `${h} h ${m} min` : `${h} h`;
  }
  const d = Math.floor(s / 86400), h = Math.round((s % 86400) / 3600);
  return h ? `${d} d ${h} h` : `${d} d`;
}

export function fmtAgo(tsOrEpoch) {
  if (!tsOrEpoch) return "—";
  const t = typeof tsOrEpoch === "number" ? tsOrEpoch * 1000 : new Date(tsOrEpoch).getTime();
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (s < 5) return "just now";
  return `${fmtDur(s)} ago`;
}

const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const hm = (d) => `${two(d.getHours())}:${two(d.getMinutes())}`;

// A past moment, the way a person says it.
export function fmtWhen(ts) {
  if (!ts) return "—";
  const d = new Date(ts); if (Number.isNaN(d.getTime())) return "—";
  const now = new Date(); const y = new Date(now); y.setDate(now.getDate() - 1);
  if (sameDay(d, now)) return `today ${hm(d)}`;
  if (sameDay(d, y)) return `yesterday ${hm(d)}`;
  const md = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return d.getFullYear() === now.getFullYear() ? md : `${md}, ${d.getFullYear()}`;
}

// A future or past clock time: "21:00" today, "Fri 17:00" within a week,
// "Aug 29, 17:00" beyond.
export function fmtClockAt(epochSec) {
  if (!epochSec) return "";
  const d = new Date(epochSec * 1000); const now = new Date();
  if (sameDay(d, now)) return hm(d);
  if (Math.abs(d - now) < 6 * 86400e3) return `${d.toLocaleDateString(undefined, { weekday: "short" })} ${hm(d)}`;
  return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}, ${hm(d)}`;
}

// Share of a subscription window (0..1). Whole percents only — that is the
// precision the provider gives — so anything below one reads "under 1%".
export function fmtQuota(fraction) {
  if (fraction == null || !Number.isFinite(fraction)) return "—";
  const p = Math.round(fraction * 100);
  return p === 0 ? "under 1%" : `${p}%`;
}

// Workers, not pods: the k8s word means nothing to the people reading this.
export function workerState(alive, known) {
  if (alive) return { label: "worker online", color: "#16a34a" };
  if (known) return { label: "worker offline", color: "#d97706" };
  return { label: "worker gone", color: "#9ca3af" };
}
